import React from "react";
import {
  ThemeProvider,
  Theme,
  StyledEngineProvider,
} from "@mui/material/styles";

import { createTheme, GLOBAL_STYLES } from "../../theme";
import { CurrentWorkspaceContext } from "../../hooks/useCurrentWorkspace";
import { ThemeModeContext, useThemeModeState } from "../../hooks/useThemeMode";

import { LocalStorageKeys } from "../../constants";
import { generateThumbnailUrl } from "../../utils/thumbnail";
import {
  BrandedAppStatus,
  IWorkspaceInfoDto,
} from "@growth-machine-llc/stridist-api-client";
import { GlobalStyles, CssBaseline, Box } from "@mui/material";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

export interface ThemeProviderProps {
  children?: React.ReactNode;
  workspace?: IWorkspaceInfoDto;
}

export function AppThemeProvider(props: ThemeProviderProps) {
  const { children, workspace } = props;
  const { primaryColor, logoUrl: logoURL, name, brandedApp } = workspace;
  const themeModeState = useThemeModeState();

  const theme = React.useMemo(
    () =>
      createTheme({
        primary: primaryColor,
        mode: themeModeState.resolvedMode,
      }),
    [primaryColor, themeModeState.resolvedMode],
  );

  // Apply dark class to document for Tailwind CSS
  React.useEffect(() => {
    const root = document.documentElement;
    if (themeModeState.resolvedMode === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [themeModeState.resolvedMode]);

  React.useMemo(() => {
    const isBrandedApp =
      // TODO_Branded_Apps: Consider treating in-progress branded apps as published here
      brandedApp?.status === BrandedAppStatus.Published;
    const generatedLogoUrl = logoURL
      ? generateThumbnailUrl({
          src: logoURL,
          scale: {
            w: 60,
            h: 60,
          },
        })
      : "";
    // TODO_Branded_Apps: Replace with useCurrentBrand hook
    const brandName =
      isBrandedApp && workspace.name ? workspace.name : "Stridist";
    window.document.title = brandName;
    (window.document.getElementById("favicon") as any).href =
      isBrandedApp && logoURL
        ? generatedLogoUrl
        : `${process.env.PUBLIC_URL}/favicon.png`;

    if (isBrandedApp && logoURL) {
      localStorage.setItem(LocalStorageKeys.FAVICON_LOGO_URL, generatedLogoUrl);
    } else {
      localStorage.setItem(
        LocalStorageKeys.FAVICON_LOGO_URL,
        `${process.env.PUBLIC_URL}/favicon.png`,
      );
    }

    localStorage.setItem(LocalStorageKeys.TITLE, brandName);
  }, [logoURL, name]);

  return (
    <ThemeModeContext.Provider value={themeModeState}>
      <CurrentWorkspaceContext.Provider value={workspace}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalStyles styles={GLOBAL_STYLES} />
            <Box
              sx={{
                minHeight: "100vh",
                bgcolor: "background.default",
                color: "text.primary",
              }}
            >
              {children}
            </Box>
          </ThemeProvider>
        </StyledEngineProvider>
      </CurrentWorkspaceContext.Provider>
    </ThemeModeContext.Provider>
  );
}
